import HostImage from "assets/images/wizard/Authorization_UserType_Host.png";
import StudentImage from "assets/images/wizard/Authorization_UserType_Student.png";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import React, { useState } from "react";
import { Button } from "antd";
import { LeftOutlined } from "@ant-design/icons";

import { RoleUser } from "../../../interfaces/interfaces";
import { CardButton } from "../../../components/Button/CardButton";
import { InputLabel, InputLabelPassword } from "../../../components/Input";
import {
  ErrorMessage,
  Label,
  SplitLine,
  StyledButton,
  Title
} from "../../../components/generic";
import {
  ContainerLogin,
  ContainerRedirect
} from "../../../components/containers";
import { useHospiNavigate } from "../../../utils/common";
import { LOGIN_ROUTE } from "../../../utils/constants";
import { useContextHospi } from "../../../context/ContextHospi";
import {
  LoadingIndicator,
  LoadingIndicatorContainer
} from "../../../components/LoadingIndicator";
import { validateEmail, validateLength } from "../../../utils/validators";

import {
  StepDescription,
  StepTitle,
  AlreadyRegisteredContainer
} from "../../../components/Wizard/Student/styles";
import {
  CardsContainer,
  FormContainer,
  GoBackButton,
  UserTypeStep,
  RegisterTitle
} from "./styles";

const buttonImages = {
  host: HostImage,
  student: StudentImage
};

export const Register = () => {
  const navigate = useHospiNavigate();
  const { t, i18n } = useTranslation("login");
  const [selectedRole, setSelectedRole] = useState<RoleUser | null>();
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [newUserIsCreated, setNewUserIsCreated] = useState(false);
  const [values, setValues] = useState<{
    email: string;
    password: string;
    confirmPassword: string;
  }>({
    email: "",
    password: "",
    confirmPassword: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showEmailError, setShowEmailError] = useState(false);
  const [showPassError, setShowPassError] = useState(false);

  const { signUp } = useContextHospi();
  const emailValidationStatus = validateEmail({ line: values.email });
  const passwordValidationStatus = validateLength({
    line: values.password,
    params: { min: 8, max: 20 }
  });

  const handleRegister = () => {
    setIsLoading(false);

    if (values.password !== values.confirmPassword) {
      setLoginError(t("error.unequalPasswords"));
      return;
    }
    if (!selectedRole) {
      return;
    }
    setIsLoading(true);
    signUp(values.email, values.password, selectedRole)
      .then((data) => {
        setNewUserIsCreated(true);
        setIsLoading(false);
        window.dataLayer.push({
          event: "start_registration_" + selectedRole,
          user_id: data.id,
          user_type: selectedRole
        });
      })
      .catch((err) => {
        setLoginError(err.message);
        setIsLoading(false);
      });
  };

  if (newUserIsCreated) {
    return (
      <ContainerLogin>
        <ContainerRedirect>
          <Title m="0 0 24px 0">{t("message.title")}</Title>
          <Label lineHeight="150%" p="0">
            {t("message.text")}
          </Label>
        </ContainerRedirect>
        <Button
          size="large"
          style={{
            width: "100%",
            marginTop: "24px"
          }}
          type="primary"
          onClick={() => {
            setNewUserIsCreated((prev) => !prev);
            navigate(LOGIN_ROUTE);
          }}
        >
          {t("message.buttonTitle")}
        </Button>
      </ContainerLogin>
    );
  }

  return (
    <div>
      {!showRegisterForm ? (
        <UserTypeStep>
          <StepTitle>{t(`register.wizard.title`)}</StepTitle>
          <StepDescription>{t(`register.wizard.description`)}</StepDescription>
          <CardsContainer>
            {(["host", "student"] as RoleUser[]).map((role) => (
              <CardButton
                key={role}
                onClick={() => {
                  setSelectedRole(role);
                  setShowRegisterForm(true);
                }}
                icon={<img src={buttonImages[role]} alt="" />}
                label={t(`register.wizard.${role}.title`)}
                description={t(`register.wizard.${role}.description`)}
                selected={selectedRole === role}
              />
            ))}
          </CardsContainer>

          <AlreadyRegisteredContainer>
            <div>
              {t(`register.wizard.alreadyAccount`)}
              <NavLink
                to={`/${i18n.language}${LOGIN_ROUTE.slice(5)}`}
                style={{
                  marginLeft: "5px",
                  textDecoration: "underline"
                }}
              >
                {t(`register.buttonSecond`)}
              </NavLink>
            </div>
          </AlreadyRegisteredContainer>
        </UserTypeStep>
      ) : (
        <FormContainer>
          <RegisterTitle>
            {t(`register.wizard.registerAs.${selectedRole}`)}
            <GoBackButton
              onClick={() => {
                setShowRegisterForm(false);
              }}
            >
              <LeftOutlined />
            </GoBackButton>
          </RegisterTitle>
          <form
            style={{ width: "100%" }}
            onSubmit={(e) => {
              e.preventDefault();
              return false;
            }}
          >
            <InputLabel
              title={t("login.email")}
              type="email"
              placeholder={t("login.placeholderemail")}
              onChange={(e) => {
                setValues((prev) => ({
                  ...prev,
                  email: e.target.value
                }));
              }}
              onBlur={() => {
                setShowEmailError(true);
              }}
              onFocus={() => {
                setShowEmailError(false);
              }}
            >
              <ErrorMessage
                error={!emailValidationStatus.state && showEmailError}
              >
                {t("error.wrongEmail")}
              </ErrorMessage>
            </InputLabel>
            <InputLabelPassword
              title={t("login.password")}
              type="password"
              placeholder={"********"}
              onChange={(e) => {
                setValues((prev) => ({
                  ...prev,
                  password: e.target.value
                }));
              }}
              onBlur={() => {
                setShowPassError(true);
              }}
              onFocus={() => {
                setShowPassError(false);
              }}
            >
              <ErrorMessage
                error={!passwordValidationStatus.state && showPassError}
              >
                {t("error.wrongPassword")}
              </ErrorMessage>
            </InputLabelPassword>
            <InputLabelPassword
              title={t("register.confirmPassword")}
              type="password"
              placeholder={"********"}
              onChange={(e) => {
                setValues((prev) => ({
                  ...prev,
                  confirmPassword: e.target.value
                }));
              }}
            />
            {loginError ? (
              <Label p="0" fontColor={"red"} fontWeight="400">
                {loginError}
              </Label>
            ) : null}
            <StyledButton
              fontWeight="400"
              w="100%"
              m="24px 0 24px 0"
              onClick={handleRegister}
            >
              {t(`register.buttonFirst`)}
            </StyledButton>
            <SplitLine>
              <hr />
              <label>{t("login.splitline")}</label>
            </SplitLine>
            <StyledButton
              outline
              fontWeight="400"
              w="100%"
              m="24px 0 0 0"
              onClick={() => {
                navigate(LOGIN_ROUTE);
              }}
            >
              {t(`register.buttonSecond`)}
            </StyledButton>
          </form>
          {isLoading ? (
            <LoadingIndicatorContainer>
              <LoadingIndicator />
            </LoadingIndicatorContainer>
          ) : null}
        </FormContainer>
      )}
    </div>
  );
};
